import React, { useState, useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartData,
  ChartOptions
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import { mockTimeSeriesData } from '../../data/mockAnalyticsData';
import LoadingSkeleton from '../ui/LoadingSkeleton';
import { RefreshCw } from 'lucide-react';
import { supabaseClient } from '../../utils/supabaseClient';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface TimeSeriesChartsProps {
  isLoading: boolean;
  shopname: string;
  dateRange: { start: Date; end: Date };
}

const TimeSeriesCharts: React.FC<TimeSeriesChartsProps> = ({ isLoading, shopname, dateRange }) => {
  const [timeRange, setTimeRange] = useState<'3m' | '6m' | '1y'>('6m');
  const { ratingTrend, reviewCountTrend } = mockTimeSeriesData;
  const [loading, setLoading] = useState(true);
  const [chartData, setChartData] = useState([]);
  const [aggregationUnit, setAggregationUnit] = useState<'day' | 'week' | 'month' | 'year'>('month');
  const [ratingLabels, setRatingLabels] = useState<string[]>([]);
  const [ratingData, setRatingData] = useState<number[]>([]);
  const [reviewCountData, setReviewCountData] = useState<number[]>([]);
  const [replyCountData, setReplyCountData] = useState<number[]>([]);
  const [targetData, setTargetData] = useState<number[]>([]);
  const now = new Date();
  let rangeDays = 180;
  if (timeRange === '3m') rangeDays = 90;
  if (timeRange === '1y') rangeDays = 365;
  const endDate = now;
  const startDate = new Date(now.getTime() - rangeDays * 24 * 60 * 60 * 1000);

  const commonOptions: ChartOptions<'line' | 'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        titleColor: '#334155',
        bodyColor: '#334155',
        borderColor: '#e2e8f0',
        borderWidth: 1,
        padding: 10,
        boxPadding: 6,
        usePointStyle: true,
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: '#334155',
        },
      },
      y: {
        grid: {
          color: 'rgba(226, 232, 240, 0.5)',
        },
        ticks: {
          padding: 10,
          color: '#334155',
        },
      },
    },
  };

  const ratingTrendData = {
    day: {
      labels: Array.from({ length: 30 }, (_, i) => `${i + 1}日`),
      data: Array.from({ length: 30 }, () => 4 + Math.random() * 0.5),
      targetData: Array.from({ length: 30 }, () => 4.5)
    },
    week: {
      labels: Array.from({ length: 12 }, (_, i) => `${i + 1}週`),
      data: Array.from({ length: 12 }, () => 4 + Math.random() * 0.5),
      targetData: Array.from({ length: 12 }, () => 4.5)
    },
    month: ratingTrend,
    year: {
      labels: ['2020年', '2021年', '2022年', '2023年', '2024年'],
      data: [4.1, 4.2, 4.3, 4.4, 4.5],
      targetData: [4.5, 4.5, 4.5, 4.5, 4.5]
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const daysAgoStart = Math.floor((now.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
      const daysAgoEnd = Math.floor((now.getTime() - endDate.getTime()) / (1000 * 60 * 60 * 24));
      // console.log('日付範囲:', { daysAgoStart, daysAgoEnd, shopname });
      
      let query = supabaseClient
        .from('reviews')
        .select('starrating, reviewdate_data, ownerreplied', { count: 'exact' })
        .eq('shopname', shopname);
      
      const { data, error } = await query;
      // console.log('Supabase取得データ:', data, 'エラー:', error);
      
      if (!error && data) {
        let groupMap: { [key: string]: number[] } = {};
        let reviewCountMap: { [key: string]: number } = {};
        let replyCountMap: { [key: string]: number } = {};
        let labelArr: string[] = [];
        let groupCount = 0;
        
        if (aggregationUnit === 'day') {
          const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
          groupCount = Math.floor((now.getTime() - firstDayOfMonth.getTime()) / (1000 * 60 * 60 * 24)) + 1;
          for (let i = 0; i < groupCount; i++) {
            const date = new Date(now.getFullYear(), now.getMonth(), i + 1);
            const nextDate = new Date(date);
            nextDate.setDate(date.getDate() + 1);
            
            // 評価推移用のデータ（累積）
            const cumulativeGroup = data.filter((d: any) => {
              const reviewDate = new Date(now.getTime() - d.reviewdate_data * 24 * 60 * 60 * 1000);
              return reviewDate <= date;
            });
            
            // レビュー数推移用のデータ（期間内）
            const periodGroup = data.filter((d: any) => {
              const reviewDate = new Date(now.getTime() - d.reviewdate_data * 24 * 60 * 60 * 1000);
              return reviewDate >= date && reviewDate < nextDate;
            });
            
            const month = date.getMonth() + 1;
            const day = date.getDate();
            const label = `${month}/${day}`;
            groupMap[label] = cumulativeGroup.map((d: any) => Number(d.starrating)).filter((n: number) => !isNaN(n));
            reviewCountMap[label] = periodGroup.length;
            replyCountMap[label] = periodGroup.filter((d: any) => d.ownerreplied === 'Yes').length;
            labelArr.push(label);
          }
        } else if (aggregationUnit === 'week') {
          const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
          const firstWeekNumber = Math.ceil(firstDayOfMonth.getDate() / 7);
          const currentWeekNumber = Math.ceil(now.getDate() / 7);
          groupCount = currentWeekNumber - firstWeekNumber + 1;
          for (let i = 0; i < groupCount; i++) {
            const weekNumber = firstWeekNumber + i;
            const startDate = new Date(now.getFullYear(), now.getMonth(), (weekNumber - 1) * 7 + 1);
            const endDate = new Date(now.getFullYear(), now.getMonth(), weekNumber * 7);
            
            // 評価推移用のデータ（累積）
            const cumulativeGroup = data.filter((d: any) => {
              const reviewDate = new Date(now.getTime() - d.reviewdate_data * 24 * 60 * 60 * 1000);
              return reviewDate <= endDate;
            });
            
            // レビュー数推移用のデータ（期間内）
            const periodGroup = data.filter((d: any) => {
              const reviewDate = new Date(now.getTime() - d.reviewdate_data * 24 * 60 * 60 * 1000);
              return reviewDate >= startDate && reviewDate <= endDate;
            });
            
            const label = `${now.getMonth() + 1}月第${weekNumber}週`;
            groupMap[label] = cumulativeGroup.map((d: any) => Number(d.starrating)).filter((n: number) => !isNaN(n));
            reviewCountMap[label] = periodGroup.length;
            replyCountMap[label] = periodGroup.filter((d: any) => d.ownerreplied === 'Yes').length;
            labelArr.push(label);
          }
        } else if (aggregationUnit === 'month') {
          groupCount = now.getMonth() + 1;
          for (let i = 0; i < groupCount; i++) {
            const month = i + 1;
            const startDate = new Date(now.getFullYear(), month - 1, 1);
            const endDate = new Date(now.getFullYear(), month, 0); // 月末
            
            // 評価推移用のデータ（累積）
            const cumulativeGroup = data.filter((d: any) => {
              const reviewDate = new Date(now.getTime() - d.reviewdate_data * 24 * 60 * 60 * 1000);
              return reviewDate <= endDate;
            });
            
            // レビュー数推移用のデータ（期間内）
            const periodGroup = data.filter((d: any) => {
              const reviewDate = new Date(now.getTime() - d.reviewdate_data * 24 * 60 * 60 * 1000);
              return reviewDate >= startDate && reviewDate <= endDate;
            });
            
            const label = `${month}月`;
            groupMap[label] = cumulativeGroup.map((d: any) => Number(d.starrating)).filter((n: number) => !isNaN(n));
            reviewCountMap[label] = periodGroup.length;
            replyCountMap[label] = periodGroup.filter((d: any) => d.ownerreplied === 'Yes').length;
            labelArr.push(label);
          }
        } else if (aggregationUnit === 'year') {
          const oldestDate = new Date(Math.min(...data.map((d: any) => now.getTime() - d.reviewdate_data * 24 * 60 * 60 * 1000)));
          const oldestYear = oldestDate.getFullYear();
          const currentYear = now.getFullYear();
          groupCount = currentYear - oldestYear + 1;
          for (let i = 0; i < groupCount; i++) {
            const year = oldestYear + i;
            const startDate = new Date(year, 0, 1);
            const endDate = new Date(year, 11, 31); // 年末
            
            // 評価推移用のデータ（累積）
            const cumulativeGroup = data.filter((d: any) => {
              const reviewDate = new Date(now.getTime() - d.reviewdate_data * 24 * 60 * 60 * 1000);
              return reviewDate <= endDate;
            });
            
            // レビュー数推移用のデータ（期間内）
            const periodGroup = data.filter((d: any) => {
              const reviewDate = new Date(now.getTime() - d.reviewdate_data * 24 * 60 * 60 * 1000);
              return reviewDate >= startDate && reviewDate <= endDate;
            });
            
            const label = `${year}年`;
            groupMap[label] = cumulativeGroup.map((d: any) => Number(d.starrating)).filter((n: number) => !isNaN(n));
            reviewCountMap[label] = periodGroup.length;
            replyCountMap[label] = periodGroup.filter((d: any) => d.ownerreplied === 'Yes').length;
            labelArr.push(label);
          }
        }
        
        // console.log('グループ化結果:', groupMap);
        const avgArr = labelArr.map(label => {
          const arr = groupMap[label];
          if (!arr || arr.length === 0) return 0;
          return arr.reduce((a, b) => a + b, 0) / arr.length;
        });
        // console.log('平均配列:', avgArr);
        
        setRatingLabels(labelArr);
        setRatingData(avgArr);
        setReviewCountData(labelArr.map(label => reviewCountMap[label] || 0));
        setReplyCountData(labelArr.map(label => replyCountMap[label] || 0));
        setTargetData(avgArr.map(() => 4.5));
      } else {
        setRatingLabels([]);
        setRatingData([]);
        setReviewCountData([]);
        setReplyCountData([]);
        setTargetData([]);
      }
      setLoading(false);
    };
    fetchData();
  }, [shopname, aggregationUnit, timeRange]);

  const ratingChartData: ChartData<'line'> = {
    labels: ratingLabels,
    datasets: [
      {
        label: 'Average Rating',
        data: ratingData,
        borderColor: '#007AFF',
        backgroundColor: 'rgba(0, 122, 255, 0.1)',
        fill: true,
        tension: 0.3,
      }
    ],
  };

  const reviewCountChartData: ChartData<'bar'> = {
    labels: ratingLabels,
    datasets: [
      {
        label: 'レビュー数',
        data: reviewCountData,
        backgroundColor: 'rgba(0, 122, 255, 0.7)',
        borderRadius: 4,
      },
      {
        label: '返信数',
        data: replyCountData,
        backgroundColor: 'rgba(52, 211, 153, 0.7)',
        borderRadius: 4,
      },
    ],
  };

  const handleRefresh = () => {
    setLoading(true);
    // Replace with actual refresh logic
    setLoading(false);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <LoadingSkeleton className="h-80" />
        <LoadingSkeleton className="h-80" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-800">評価推移</h3>
          <div className="flex items-center space-x-4 bg-white">
            <select
              value={aggregationUnit}
              onChange={e => setAggregationUnit(e.target.value as any)}
              className="text-sm text-gray-600 bg-white px-3 py-2 border border-gray-200 rounded-md"
            >
              <option value="day">日別</option>
              <option value="week">週別</option>
              <option value="month">月別</option>
              <option value="year">年別</option>
            </select>
          </div>
        </div>
        <div className="h-64 bg-white">
          <Line options={commonOptions} data={ratingChartData} />
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-800">レビュー数推移</h3>
          <div className="flex items-center space-x-4 bg-white">
            <select
              value={aggregationUnit}
              onChange={e => setAggregationUnit(e.target.value as any)}
              className="text-sm text-gray-600 bg-white px-3 py-2 border border-gray-200 rounded-md"
            >
              <option value="day">日別</option>
              <option value="week">週別</option>
              <option value="month">月別</option>
              <option value="year">年別</option>
            </select>
          </div>
        </div>
        <div className="h-64 bg-white">
          <Bar options={commonOptions} data={reviewCountChartData} />
        </div>
      </div>
    </div>
  );
};

export default TimeSeriesCharts;